import { Head } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { TravelTrackerSubNav } from "@/components/travel_tracker/TravelTrackerSubNav"
import { WeekStrip } from "@/components/travel_tracker/WeekStrip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type {
  SummaryDay,
  SummaryDepartment,
  SummaryPaging,
  SummaryPerson,
  WeekOption,
} from "@/types/travel_tracker"

const DESCRIPTION =
  "Where everyone has been across one week — department by department, each person's seven days side by side, with the history imported from the old tracker included."

export default function TravelTrackerSummary({
  week,
  weeks,
  days,
  departments,
  paging,
}: {
  week: WeekOption
  weeks: WeekOption[]
  days: SummaryDay[]
  departments: SummaryDepartment[]
  paging: SummaryPaging
}) {
  // The person column, the seven days, and the trailing Days total.
  const columns = days.length + 2
  const people = departments.reduce((sum, group) => sum + group.people.length, 0)
  const totalDays = departments.reduce((sum, group) => sum + group.recorded, 0)

  return (
    <>
      <Head title="Travel Summary">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Travel Summary" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Travel Tracker"
          description="Where people have been, a week at a time."
          tabs={<TravelTrackerSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <WeekStrip week={week} weeks={weeks} paging={paging} path="/travel_tracker/summary" />

          <p className="mt-4 text-sm text-ink-muted">
            {people === 0
              ? `Nobody recorded travel in ${week.label}.`
              : `${totalDays} ${totalDays === 1 ? "day" : "days"} recorded by ${people} ${
                  people === 1 ? "person" : "people"
                } in ${week.label}.`}
          </p>

          {people > 0 && (
            <div className="mt-4">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell sticky>Person</TableHeaderCell>
                    {days.map((day) => (
                      <TableHeaderCell key={day.date} className="text-center">
                        <span className="block">{day.day_name}</span>
                        <span className="block font-normal text-ink-muted">
                          {day.month_label} {day.day_label}
                        </span>
                      </TableHeaderCell>
                    ))}
                    <TableHeaderCell numeric>Days</TableHeaderCell>
                  </TableRow>
                </TableHead>

                {departments.map((department) => (
                  /*
                    One tbody per department. `.table tbody` carries
                    divide-y, so a fresh one restarts the divider run and the
                    group header does not get a hairline welded to the row above
                    it from the previous department.
                  */
                  <TableBody key={department.name}>
                    <TableRow className="bg-surface">
                      {/*
                        The label is sticky, not the cell. A cell spanning every
                        column fills its row exactly, so `position: sticky` has no
                        slack and the name would simply scroll away — and
                        .cell-sticky is bg-page, which would paint over this row's
                        bg-surface. A sticky span inside the wide cell has room.
                        `left-3` matches the cell's own px-3.
                      */}
                      <TableCell colSpan={columns} className="py-3">
                        <span className="sticky left-3 inline-block">
                          <span className="text-sm font-semibold text-ink-display">
                            {department.name}
                          </span>
                          <span className="ml-2 text-ink-muted">
                            {department.recorded}{" "}
                            {department.recorded === 1 ? "day" : "days"} across{" "}
                            {department.people.length}{" "}
                            {department.people.length === 1 ? "person" : "people"}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>
                    {department.people.map((person) => (
                      <PersonRow key={person.id} person={person} days={days} />
                    ))}
                  </TableBody>
                ))}
              </Table>
            </div>
          )}

          <p className="mt-4 text-xs text-ink-muted">
            A day with no place recorded against it has no record at all, so a dash means nothing was
            logged rather than nowhere was visited.
          </p>
        </div>
      </AppShell>
    </>
  )
}

function PersonRow({ person, days }: { person: SummaryPerson; days: SummaryDay[] }) {
  return (
    <TableRow>
      <TableCell sticky nowrap>
        {person.name}
      </TableCell>
      {person.cells.map((code, index) => (
        /*
          Deliberately not `nowrap`. A long code — "Excelsior Springs JCC MO" —
          held on one line forces a 153px column, and seven of those put the
          table back into its own scroller. Wrapping keeps the whole week on
          screen, which is the entire reason the summary reads a week.
        */
        <TableCell key={days[index].date} className="text-center">
          {code ? (
            code
          ) : (
            <span className="text-ink-muted">
              <span aria-hidden="true">&mdash;</span>
              <span className="sr-only">
                {person.name} recorded nothing on {days[index].day_name}{" "}
                {days[index].month_label} {days[index].day_label}
              </span>
            </span>
          )}
        </TableCell>
      ))}
      <TableCell numeric>{person.recorded}</TableCell>
    </TableRow>
  )
}
